import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import axios from "axios";

const AUTH = {
  headers: {
    Authorization: `Bearer ${process.env.REACT_APP_SUPABASE_ANON_KEY}`,
  },
};

const SEASON = 2026; // switch to 2026 when standings exist

const AFC_DIVISIONS = ["EAST", "NORTH", "SOUTH", "WEST"];
const NFC_DIVISIONS = ["EAST", "NORTH", "SOUTH", "WEST"];

function StandingsPage() {
  const [standings, setStandings] = useState([]);
  const [view, setView] = useState("conference"); // conference | division | overall
  const [loading, setLoading] = useState(true);

  // Navbar (same pattern as TeamPage)
  const [openDropdown, setOpenDropdown] = useState(null);
  const teamsRef = useRef(null);
  const leadersRef = useRef(null);
  const playersRef = useRef(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileOpenSection, setMobileOpenSection] = useState(null);
  const [searchExpanded, setSearchExpanded] = useState(false);
  const [navbarSearchTerm, setNavbarSearchTerm] = useState("");
  const [navbarResults, setNavbarResults] = useState([]);
  const [navbarSelectedIndex, setNavbarSelectedIndex] = useState(-1);
  const navbarInputRef = useRef(null);
  const navbarContainerRef = useRef(null);

  const [sortConfig, setSortConfig] = useState({
   key: "wins",
   direction: "desc",
  });

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (navbarContainerRef.current && !navbarContainerRef.current.contains(e.target)) {
        setSearchExpanded(false);
        setNavbarResults([]);
        setNavbarSelectedIndex(-1);
      }
      if (
        teamsRef.current && !teamsRef.current.contains(e.target) &&
        leadersRef.current && !leadersRef.current.contains(e.target) &&
        playersRef.current && !playersRef.current.contains(e.target)
      ) {
        setOpenDropdown(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (!navbarSearchTerm.trim()) {
      setNavbarResults([]);
      setNavbarSelectedIndex(-1);
      return;
    }
    const search = async () => {
      try {
        const res = await axios.get(
          `https://pfuzmgbswynewjxsweqd.supabase.co/functions/v1/get-players?search=${encodeURIComponent(navbarSearchTerm)}`,
          AUTH
        );
        setNavbarResults(res.data.slice(0, 12));
        setNavbarSelectedIndex(-1);
      } catch (err) {
        console.error(err);
      }
    };
    const t = setTimeout(search, 400);
    return () => clearTimeout(t);
  }, [navbarSearchTerm]);

  useEffect(() => {
    const fetchStandings = async () => {
      try {
        const res = await axios.get(
          `https://pfuzmgbswynewjxsweqd.supabase.co/functions/v1/get-standings?season=${SEASON}`,
          AUTH
        );
        setStandings(res.data || []);
      } catch (err) {
        console.error("Standings error:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchStandings();
  }, []);

  const handleSort = (key) => {
  setSortConfig((prev) => ({
    key,
    direction: prev.key === key && prev.direction === "desc" ? "asc" : "desc",
  }));
};

const sortTeams = (list) => {
  const { key, direction } = sortConfig;
  return [...list].sort((a, b) => {
    let aVal = a[key];
    let bVal = b[key];

    // numeric-ish fields
    if (
      [
        "wins",
        "losses",
        "ties",
        "win_streak",
        "points_for",
        "points_against",
        "point_differential",
        "playoff_seed",
      ].includes(key)
    ) {
      aVal = Number(aVal) || 0;
      bVal = Number(bVal) || 0;
      return direction === "desc" ? bVal - aVal : aVal - bVal;
    }

    // text fields (team name, records)
    aVal = String(aVal ?? "").toLowerCase();
    bVal = String(bVal ?? "").toLowerCase();
    if (aVal < bVal) return direction === "asc" ? -1 : 1;
    if (aVal > bVal) return direction === "asc" ? 1 : -1;
    return 0;
  });
};

  const afc = sortTeams(standings.filter((t) => t.conference === "AFC"));
  const nfc = sortTeams(standings.filter((t) => t.conference === "NFC"));
  const overall = sortTeams(standings);

  const divisionBlock = (conference, divisions) =>
    divisions.map((div) => {
      const teams = sortTeams(
        standings.filter(
          (t) =>
            t.conference === conference &&
            String(t.division || "").toUpperCase() === div
        )
      );
      return { key: `${conference}-${div}`, title: `${conference} ${div}`, teams };
    });

  const columns = [
  { key: "rank", label: "#", sortable: false, className: "w-8 text-center" },
  { key: "full_name", label: "Team", sortable: true, className: "min-w-[140px] text-left" },
  { key: "wins", label: "W", sortable: true, className: "w-10 text-center" },
  { key: "losses", label: "L", sortable: true, className: "w-10 text-center" },
  { key: "ties", label: "T", sortable: true, className: "w-10 text-center" },
  { key: "overall_record", label: "OVR", sortable: true, className: "w-16 text-center" },
  { key: "conference_record", label: "CONF", sortable: true, className: "w-16 text-center" },
  { key: "division_record", label: "DIV", sortable: true, className: "w-16 text-center" },
  { key: "win_streak", label: "STRK", sortable: true, className: "w-14 text-center" },
  { key: "points_for", label: "PF", sortable: true, className: "w-12 text-center" },
  { key: "points_against", label: "PA", sortable: true, className: "w-12 text-center" },
];

    const renderTable = (teams, showRank = true) => {
    const colCount = showRank ? 11 : 10;

  return (
    <div className="w-full overflow-x-auto -mx-2 px-2">
      <table className="w-full min-w-[860px] table-fixed text-sm">
        <thead>
          <tr className="border-b border-zinc-700 text-zinc-500">
            {showRank && (
              <th
                className="py-3 px-1 text-center font-medium w-[6%]"
              >
                #
              </th>
            )}
            <th
              onClick={() => handleSort("full_name")}
              className={`py-3 px-1 text-left font-medium cursor-pointer hover:text-red-400 select-none ${showRank ? "w-[22%]" : "w-[28%]"}`}
            >
              Team
              {sortConfig.key === "full_name" && (
                <span className="ml-1 text-red-400">
                  {sortConfig.direction === "desc" ? "↓" : "↑"}
                </span>
              )}
            </th>
            {[
              { key: "wins", label: "W" },
              { key: "losses", label: "L" },
              { key: "ties", label: "T" },
              { key: "overall_record", label: "OVR" },
              { key: "conference_record", label: "CONF" },
              { key: "division_record", label: "DIV" },
              { key: "win_streak", label: "STRK" },
              { key: "points_for", label: "PF" },
              { key: "points_against", label: "PA" },
            ].map((col) => (
              <th
                key={col.key}
                onClick={() => handleSort(col.key)}
                className="py-3 px-1 text-center font-medium cursor-pointer hover:text-red-400 select-none"
                style={{ width: `${(showRank ? 72 : 72) / 9}%` }}
              >
                {col.label}
                {sortConfig.key === col.key && (
                  <span className="ml-1 text-red-400">
                    {sortConfig.direction === "desc" ? "↓" : "↑"}
                  </span>
                )}
              </th>
            ))}
          </tr>
        </thead>

        <tbody>
          {teams.map((t, i) => {
            const streak =
              t.win_streak > 0
                ? `W${t.win_streak}`
                : t.win_streak < 0
                ? `L${Math.abs(t.win_streak)}`
                : "—";

            return (
              <tr
                key={t.team_id || t.abbreviation}
                className="border-b border-zinc-800 hover:bg-zinc-800/70 transition"
              >
                {showRank && (
                  <td className="py-3 px-1 text-center text-zinc-500 font-mono">
                    {i + 1}
                  </td>
                )}
                <td className="py-3 px-1 text-left">
                  <Link to={`/team/${t.abbreviation}`} className="hover:text-red-400">
                    <div className="font-bold leading-tight truncate">
                      {t.full_name || t.abbreviation}
                    </div>
                    <div className="text-xs text-zinc-500">
                      {t.abbreviation}
                      {t.playoff_seed ? ` · #${t.playoff_seed}` : ""}
                    </div>
                  </Link>
                </td>
                <td className="py-3 px-1 text-center font-mono">{t.wins}</td>
                <td className="py-3 px-1 text-center font-mono">{t.losses}</td>
                <td className="py-3 px-1 text-center font-mono">{t.ties}</td>
                <td className="py-3 px-1 text-center font-mono">{t.overall_record}</td>
                <td className="py-3 px-1 text-center font-mono text-zinc-300">{t.conference_record}</td>
                <td className="py-3 px-1 text-center font-mono text-zinc-300">{t.division_record}</td>
                <td
                  className={`py-3 px-1 text-center font-bold ${
                    t.win_streak > 0
                      ? "text-green-400"
                      : t.win_streak < 0
                      ? "text-red-400"
                      : "text-zinc-400"
                  }`}
                >
                  {streak}
                </td>
                <td className="py-3 px-1 text-center font-mono">{t.points_for}</td>
                <td className="py-3 px-1 text-center font-mono">{t.points_against}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center text-3xl text-white">
        Loading standings...
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white font-sans">
      {/* NAVBAR — same structure as TeamPage */}
      <nav className="fixed top-0 w-full bg-black/90 backdrop-blur-md z-50 border-b border-red-500/30">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <img src="/images/HOFLogoSquare.png" alt="HOF Logo" className="w-12 h-12 object-contain" />
            <div>
              <div className="text-2xl font-black tracking-tighter">HELMETS OFF</div>
              <div className="text-[10px] text-red-400 -mt-1">FOOTBALL</div>
            </div>
          </Link>

          <div className="flex-1 hidden md:block" />

          <div className="flex items-center gap-4">
            <div className="relative" ref={navbarContainerRef}>
              {!searchExpanded ? (
                <button
                  onClick={() => {
                    setSearchExpanded(true);
                    setTimeout(() => navbarInputRef.current?.focus(), 50);
                  }}
                  className="p-2 text-zinc-400 hover:text-white transition"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </button>
              ) : (
                <div className="relative w-64 sm:w-72">
                  <input
                    ref={navbarInputRef}
                    type="text"
                    value={navbarSearchTerm}
                    onChange={(e) => setNavbarSearchTerm(e.target.value)}
                    placeholder="Search players..."
                    className="w-full bg-zinc-900 border border-zinc-700 rounded-full py-3 px-5 pl-12 text-sm focus:outline-none focus:border-red-500"
                    autoFocus
                  />
                  <span className="absolute left-4 top-3.5 text-zinc-400">
                    <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                  </span>
                  {navbarResults.length > 0 && (
                    <div className="absolute mt-2 w-full bg-zinc-900 border border-zinc-700 rounded-2xl shadow-2xl overflow-hidden z-50 max-h-64 overflow-y-auto">
                      {navbarResults.map((item, index) => (
                        <Link
                          key={`${item.type}-${item.bdl_id || item.id}`}
                          to={item.type === "team" ? `/team/${item.abbreviation}` : `/player/${item.bdl_id || item.id}`}
                          className={`block px-4 py-3 hover:bg-zinc-800 ${navbarSelectedIndex === index ? "bg-zinc-800" : ""}`}
                          onClick={() => {
                            setNavbarSearchTerm("");
                            setNavbarResults([]);
                            setSearchExpanded(false);
                          }}
                        >
                          {item.type === "team" ? (
                            <>
                              <span className="font-medium">{item.name}</span>
                              <span className="text-zinc-500 text-sm"> • Team</span>
                            </>
                          ) : (
                            <>
                              {item.first_name} {item.last_name} • {item.position_abbreviation}
                            </>
                          )}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="hidden md:flex items-center gap-8 text-sm font-medium">
              <div className="relative" ref={teamsRef}>
                <button onClick={() => setOpenDropdown(openDropdown === "teams" ? null : "teams")} className="hover:text-red-400 transition">
                  TEAMS
                </button>
                {openDropdown === "teams" && (
                  <div className="absolute top-full right-0 mt-3 w-48 bg-zinc-900 border border-zinc-700 rounded-2xl shadow-2xl overflow-hidden z-50">
                    <Link to="/teams" className="block px-5 py-3 hover:bg-zinc-800 font-medium" onClick={() => setOpenDropdown(null)}>All Teams</Link>
                    <Link to="/standings" className="block px-5 py-3 hover:bg-zinc-800 text-red-400" onClick={() => setOpenDropdown(null)}>Standings</Link>
                    <Link to="/Standigs" className="block px-5 py-3 hover:bg-zinc-800" onClick={() => setOpenDropdown(null)}>Standigs</Link>
                    <Link to="/Schedule" className="block px-5 py-3 hover:bg-zinc-800" onClick={() => setOpenDropdown(null)}>Schedule</Link>
                  </div>
                )}
              </div>
              <div className="relative" ref={leadersRef}>
                <button onClick={() => setOpenDropdown(openDropdown === "leaders" ? null : "leaders")} className="hover:text-red-400 transition">
                  LEADERS
                </button>
                {openDropdown === "leaders" && (
                  <div className="absolute top-full right-0 mt-3 w-48 bg-zinc-900 border border-zinc-700 rounded-2xl shadow-2xl overflow-hidden z-50">
                    <Link to="/leaderboards" className="block px-5 py-3 hover:bg-zinc-800 font-medium" onClick={() => setOpenDropdown(null)}>Season Leaders</Link>
                    <Link to="/leaderboards?stat=passing_yards" className="block px-5 py-3 hover:bg-zinc-800" onClick={() => setOpenDropdown(null)}>Passing</Link>
                    <Link to="/leaderboards?stat=rushing_yards" className="block px-5 py-3 hover:bg-zinc-800" onClick={() => setOpenDropdown(null)}>Rushing</Link>
                    <Link to="/leaderboards?stat=receiving_yards" className="block px-5 py-3 hover:bg-zinc-800" onClick={() => setOpenDropdown(null)}>Receiving</Link>
                  </div>
                )}
              </div>
              <div className="relative" ref={playersRef}>
                <button onClick={() => setOpenDropdown(openDropdown === "players" ? null : "players")} className="hover:text-red-400 transition">
                  PLAYERS
                </button>
                {openDropdown === "players" && (
                  <div className="absolute top-full right-0 mt-3 w-48 bg-zinc-900 border border-zinc-700 rounded-2xl shadow-2xl overflow-hidden z-50">
                    <Link to="/playerStats" className="block px-5 py-3 hover:bg-zinc-800 font-medium" onClick={() => setOpenDropdown(null)}>Player Stats</Link>
                    <Link to="/playerCompare" className="block px-5 py-3 hover:bg-zinc-800" onClick={() => setOpenDropdown(null)}>Player compare</Link>
                  </div>
                )}
              </div>
            </div>

            <button className="md:hidden p-2 text-zinc-400 hover:text-white" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
              {mobileMenuOpen ? "✕" : "☰"}
            </button>
          </div>
        </div>

        {mobileMenuOpen && (
          <div className="md:hidden absolute top-full left-0 w-full bg-black/95 border-b border-zinc-800 px-6 py-5 z-40">
            <Link to="/standings" className="block py-3 text-lg text-red-400" onClick={() => setMobileMenuOpen(false)}>Standings</Link>
            <Link to="/teams" className="block py-3 text-lg" onClick={() => setMobileMenuOpen(false)}>Teams</Link>
            <Link to="/leaderboards" className="block py-3 text-lg" onClick={() => setMobileMenuOpen(false)}>Leaders</Link>
            <Link to="/playerStats" className="block py-3 text-lg" onClick={() => setMobileMenuOpen(false)}>Players</Link>
          </div>
        )}
      </nav>

      <div className="pt-24 pb-12">
        <div className="max-w-7xl mx-auto px-6">
          <h1 className="text-5xl md:text-6xl font-black text-center mb-4 tracking-tighter">
            LEAGUE STANDINGS
          </h1>
          <p className="text-center text-zinc-500 mb-10">{SEASON} Season</p>

          {/* Radio view switcher */}
          <div className="flex flex-wrap justify-center gap-3 mb-12">
            {[
              { id: "conference", label: "Conference" },
              { id: "division", label: "Division" },
              { id: "overall", label: "Overall" },
            ].map((opt) => (
              <label
                key={opt.id}
                className={`cursor-pointer px-6 py-3 rounded-full border text-sm font-bold transition ${
                  view === opt.id
                    ? "bg-red-600 border-red-500 text-white"
                    : "bg-zinc-900 border-zinc-700 text-zinc-300 hover:border-red-500"
                }`}
              >
                <input
                  type="radio"
                  name="standingsView"
                  value={opt.id}
                  checked={view === opt.id}
                  onChange={() => setView(opt.id)}
                  className="sr-only"
                />
                {opt.label}
              </label>
            ))}
          </div>

          {/* CONTENT */}
          <div className="bg-zinc-900 rounded-3xl shadow-2xl overflow-hidden border border-zinc-800">
            {standings.length === 0 ? (
              <div className="p-16 text-center text-zinc-500">No standings data for {SEASON}</div>
            ) : view === "overall" ? (
              <div className="p-6 md:p-8">
                <h2 className="text-2xl font-black mb-6 text-center">Overall</h2>
                {renderTable(overall, true)}
              </div>
            ) : view === "conference" ? (
              <div className="p-6 md:p-8 space-y-12">
                <div>
                  <h2 className="text-2xl font-black mb-6 text-blue-400">AFC</h2>
                  {renderTable(afc, true)}
                </div>
                <div>
                  <h2 className="text-2xl font-black mb-6 text-red-400">NFC</h2>
                  {renderTable(nfc, true)}
                </div>
              </div>
            ) : (
              <div className="p-6 md:p-8 space-y-10">
                <div>
                  <h2 className="text-xl font-black mb-4 text-blue-400">AFC</h2>
                  <div className="space-y-8">
                    {divisionBlock("AFC", AFC_DIVISIONS).map((block) => (
                      <div key={block.key}>
                        <h3 className="text-sm font-mono tracking-widest text-zinc-500 mb-3">
                          {block.title}
                        </h3>
                        {renderTable(block.teams, false)}
                      </div>
                    ))}
                  </div>
                </div>
                <div>
                  <h2 className="text-xl font-black mb-4 text-red-400">NFC</h2>
                  <div className="space-y-8">
                    {divisionBlock("NFC", NFC_DIVISIONS).map((block) => (
                      <div key={block.key}>
                        <h3 className="text-sm font-mono tracking-widest text-zinc-500 mb-3">
                          {block.title}
                        </h3>
                        {renderTable(block.teams, false)}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <footer className="bg-black border-t border-zinc-800 py-16">
        <div className="max-w-7xl mx-auto px-6 text-center text-zinc-500">
          <p>© 2026 Helmets Off Football • Serratox</p>
        </div>
      </footer>
    </div>
  );
}

export default StandingsPage;